import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import AppointmentService from '../services/AppointmentService';
import userService from '../services/userService';
import XPortionService from '../services/XPortionService';
import ProfileImage from './ProfileImage';
import { BsCash, BsBook, BsPeople, BsPieChart, BsSpeedometer, BsGraphUp, BsPieChartFill } from 'react-icons/bs';
import './UserOrgInfo.css';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const UserOrgInfo = () => {
    const { user } = useContext(AuthContext);
    const [appointments, setAppointments] = useState([]);
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [latestXPortion, setLatestXPortion] = useState(1.0);
    const [totalOrgPortionValue, setTotalOrgPortionValue] = useState(0);
    const [theme, setTheme] = useState(document.documentElement.getAttribute('data-theme'));

    // Helper function to get user type
    const getUserType = () => {
        if (!userDetails) return "User";
        switch(userDetails.userType) {
            case 0: return "Administrator";
            case 1: return "Normal User";
            case 2: return "Manager";
            default: return "User";
        }
    };

    // Helper function to get active positions
    const getActivePositions = () => {
        if (!Array.isArray(appointments)) return [];
        return appointments
            .filter(a => !a?.endDate || new Date(a.endDate) > new Date())
            .slice(0, 3)  // Get only first 3 positions
            .map(position => ({
                title: position.positionTitle,
                unit: position.organizationalUnitName,
                role: position.roleName,
                roleImportance: position.roleImportance
            }));
    };

    // Helper function to calculate portion value
    const calculatePortionValue = (appointmentsList, xPortionValue) => {
        const baseSum = appointmentsList.reduce((acc, curr) => {
            const employmentTypeMultiplier = curr.employmentTypeMultiplier || 1.0;
            return acc + (Number(curr?.roleImportance || 0) * employmentTypeMultiplier);
        }, 0);
        return baseSum * xPortionValue;
    };

    // Update theme and charts when theme changes
    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'data-theme') {
                    setTheme(document.documentElement.getAttribute('data-theme'));
                }
            });
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });

        return () => observer.disconnect();
    }, []);

    // Function to get theme-aware colors
    const getChartColors = () => {
        const isDarkMode = theme === 'dark';
        return {
            text: isDarkMode ? '#FFFFFF' : '#000000',
            grid: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            colors: {
                individual: {
                    border: isDarkMode ? 'rgba(129, 199, 132, 1)' : 'rgba(46, 125, 50, 0.7)',
                    background: isDarkMode ? 'rgba(129, 199, 132, 0.3)' : 'rgba(46, 125, 50, 0.1)',
                    point: isDarkMode ? 'rgba(129, 199, 132, 1)' : 'rgba(46, 125, 50, 1)'
                },
                individualLog: {
                    border: isDarkMode ? 'rgba(186, 104, 200, 1)' : 'rgba(156, 39, 176, 0.7)',
                    background: isDarkMode ? 'rgba(186, 104, 200, 0.3)' : 'rgba(156, 39, 176, 0.1)',
                    point: isDarkMode ? 'rgba(186, 104, 200, 1)' : 'rgba(156, 39, 176, 1)'
                },
                cumulative: {
                    border: isDarkMode ? 'rgba(100, 181, 246, 1)' : 'rgba(25, 118, 210, 0.7)',
                    background: isDarkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.1)',
                    point: isDarkMode ? 'rgba(100, 181, 246, 1)' : 'rgba(25, 118, 210, 1)'
                },
                cumulativeLog: {
                    border: isDarkMode ? 'rgba(255, 167, 38, 1)' : 'rgba(245, 124, 0, 0.7)',
                    background: isDarkMode ? 'rgba(255, 167, 38, 0.3)' : 'rgba(245, 124, 0, 0.1)',
                    point: isDarkMode ? 'rgba(255, 167, 38, 1)' : 'rgba(245, 124, 0, 1)'
                }
            }
        };
    };

    // Function to prepare time series data for added value chart
    const prepareAddedValueChartData = () => {
        if (!appointments || appointments.length === 0) return null;

        const colors = getChartColors();

        // Sort appointments by start date
        const sortedAppointments = [...appointments].sort((a, b) => 
            new Date(a.startDate) - new Date(b.startDate)
        );

        // Prepare data points
        const labels = sortedAppointments.map(app => 
            new Date(app.startDate).toLocaleDateString()
        );

        // Calculate individual values and their logs
        const individualValues = sortedAppointments.map(app => {
            const employmentTypeMultiplier = app.employmentTypeMultiplier || 1.0;
            const actualValue = Number(app.roleImportance || 0) * employmentTypeMultiplier;
            return actualValue;
        });

        const individualLogValues = individualValues.map(value => Math.log(value + 1));

        // Calculate cumulative values and their logs
        const cumulativeValues = [];
        const cumulativeLogValues = [];
        let runningSum = 0;

        individualValues.forEach(value => {
            runningSum += value;
            cumulativeValues.push(runningSum);
            cumulativeLogValues.push(Math.log(runningSum + 1));
        });

        return {
            labels,
            datasets: [
                {
                    label: 'Individual Value',
                    data: individualValues,
                    borderColor: colors.colors.individual.border,
                    backgroundColor: colors.colors.individual.background,
                    pointBackgroundColor: colors.colors.individual.point,
                    borderWidth: 3,
                    tension: 0.3,
                    yAxisID: 'y'
                },
                {
                    label: 'Individual Value (Log)',
                    data: individualLogValues,
                    borderColor: colors.colors.individualLog.border,
                    backgroundColor: colors.colors.individualLog.background,
                    pointBackgroundColor: colors.colors.individualLog.point,
                    borderWidth: 3,
                    tension: 0.3,
                    yAxisID: 'y1'
                },
                {
                    label: 'Cumulative Value',
                    data: cumulativeValues,
                    borderColor: colors.colors.cumulative.border,
                    backgroundColor: colors.colors.cumulative.background,
                    pointBackgroundColor: colors.colors.cumulative.point,
                    borderWidth: 3,
                    tension: 0.3,
                    yAxisID: 'y',
                    fill: true
                },
                {
                    label: 'Cumulative Value (Log)',
                    data: cumulativeLogValues,
                    borderColor: colors.colors.cumulativeLog.border,
                    backgroundColor: colors.colors.cumulativeLog.background,
                    pointBackgroundColor: colors.colors.cumulativeLog.point,
                    borderWidth: 3,
                    tension: 0.3,
                    yAxisID: 'y1',
                    fill: true
                }
            ]
        };
    };

    // Function to prepare data for role distribution chart
    const prepareRoleDistributionData = () => {
        if (!appointments || appointments.length === 0) return null;

        // Sort appointments by role importance in descending order
        const sortedAppointments = [...appointments].sort((a, b) => 
            (Number(b.roleImportance) || 0) - (Number(a.roleImportance) || 0)
        );

        const labels = sortedAppointments.map(app => app.positionTitle || 'Undefined Position');
        const data = sortedAppointments.map(app => Number(app.roleImportance) || 0);

        const colors = getChartColors();

        return {
            labels,
            datasets: [{
                label: 'Role Importance',
                data,
                backgroundColor: 'rgba(25, 118, 210, 0.7)',
                borderColor: 'rgba(25, 118, 210, 1)',
                borderWidth: 1,
                borderRadius: 5,
                maxBarThickness: 50
            }]
        };
    };

    // Chart options
    const addedValueChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        size: 11
                    },
                    boxWidth: 15,
                    color: getChartColors().text
                }
            },
            title: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const value = context.raw;
                        if (context.dataset.yAxisID === 'y1') {
                            return `${context.dataset.label}: ${value.toFixed(2)} (e^${value.toFixed(2)} = ${Math.exp(value).toFixed(2)})`;
                        }
                        return `${context.dataset.label}: ${value.toFixed(2)}`;
                    }
                }
            }
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Actual Value',
                    font: {
                        size: 11
                    },
                    color: getChartColors().text
                },
                ticks: {
                    font: {
                        size: 10
                    },
                    color: getChartColors().text
                },
                grid: {
                    color: getChartColors().grid,
                    drawOnChartArea: false
                }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Logarithmic Value',
                    font: {
                        size: 11
                    },
                    color: getChartColors().text
                },
                ticks: {
                    font: {
                        size: 10
                    },
                    color: getChartColors().text
                },
                grid: {
                    color: getChartColors().grid,
                    drawOnChartArea: false
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Time',
                    font: {
                        size: 11
                    },
                    color: getChartColors().text
                },
                ticks: {
                    font: {
                        size: 10
                    },
                    color: getChartColors().text,
                    maxRotation: 45,
                    minRotation: 45
                },
                grid: {
                    color: getChartColors().grid
                }
            }
        },
        layout: {
            padding: {
                left: 10,
                right: 30,
                top: 20,
                bottom: 10
            }
        }
    };

    // Helper function to wrap text
    const wrapText = (text, maxLength = 12) => {
        if (!text || text.length <= maxLength) return [text];
        
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';
        
        for (const word of words) {
            if ((currentLine + word).length <= maxLength) {
                currentLine += (currentLine ? ' ' : '') + word;
            } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            }
        }
        if (currentLine) lines.push(currentLine);
        
        return lines;
    };

    // Update role distribution chart options
    const roleDistributionChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    display: false
                },
                ticks: {
                    color: getChartColors().text,
                    font: {
                        size: 11
                    }
                },
                border: {
                    display: false
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    color: getChartColors().text,
                    font: {
                        size: 10
                    },
                    maxRotation: 0,
                    minRotation: 0,
                    callback: function(value, index) {
                        const label = this.getLabelForValue(value);
                        const wrappedLines = wrapText(label, 12);
                        return wrappedLines;
                    }
                },
                border: {
                    display: false
                }
            }
        },
        layout: {
            padding: {
                left: 10,
                right: 30,
                top: 20,
                bottom: 40
            }
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!user?.userId) return;
            
            try {
                setLoading(true);
                const [appointmentsData, userData, xPortionData, allAppointments] = await Promise.all([
                    AppointmentService.getAppointmentsByUserId(user.userId),
                    userService.getUserById(user.userId),
                    XPortionService.getLatest(),
                    AppointmentService.getAllAppointments()
                ]);
                
                setAppointments(appointmentsData);
                setUserDetails(userData);
                const xPortionValue = xPortionData.value || 1.0;
                setLatestXPortion(xPortionValue);

                // Calculate total organization portion value
                const totalPortion = calculatePortionValue(allAppointments, xPortionValue);
                setTotalOrgPortionValue(totalPortion);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user]);

    if (loading) {
        return <div className="user-org-info-loading-spinner" />;
    }

    if (error) {
        return (
            <div className="user-org-info-error-message">
                {error}
            </div>
        );
    }

    // Calculate metrics
    const currentPositions = appointments.filter(a => !a?.endDate || new Date(a.endDate) > new Date()).length;
    
    // Simple sum of role importance
    const roleImportanceSum = appointments.reduce((acc, curr) => {
        return acc + Number(curr?.roleImportance || 0);
    }, 0);

    // Calculate added value - sum all values first, then take log for more meaningful number
    const addedValue = Math.log(
        appointments.reduce((acc, curr) => {
            // For each appointment: roleImportance × employmentTypeMultiplier
            const appointmentValue = Number(curr?.roleImportance || 0) * Number(curr?.employmentTypeMultiplier || 1.0);
            // Sum up all appointment values
            return acc + appointmentValue;
        }, 0) + 1
    );

    // Calculate portion value
    const baseSum = appointments.reduce((acc, curr) => {
        const employmentTypeMultiplier = curr.employmentTypeMultiplier || 1.0;
        return acc + (Number(curr?.roleImportance || 0) * employmentTypeMultiplier);
    }, 0);
    const portionValue = baseSum * latestXPortion;

    // Calculate portion percentage
    const portionPercentage = totalOrgPortionValue > 0 
        ? ((portionValue / totalOrgPortionValue) * 100).toFixed(1) 
        : '0.0';

    // Placeholder for performance indicator
    const performanceIndicator = 0;

    const metrics = [
        { icon: <BsCash />, value: currentPositions, label: "Current Positions" },
        { icon: <BsBook />, value: roleImportanceSum.toFixed(2), label: "Role Importance" },
        { icon: <BsPeople />, value: addedValue.toFixed(2), label: "Added Value" },
        { 
            icon: <BsPieChart />, 
            value: portionValue.toFixed(2), 
            label: "Portion Value",
            subLabel: `${portionPercentage}% of Organization`
        },
        { icon: <BsSpeedometer />, value: performanceIndicator.toFixed(2), label: "Performance Indicator" }
    ];

    const activePositions = getActivePositions();

    return (
        <div className="user-org-info">
            <Container>
                {/* Profile Section */}
                <div className={`user-org-info-profile-section ${activePositions.length > 0 ? 'has-positions' : ''}`}>
                    <Row>
                        <Col md={activePositions.length > 0 ? 6 : 12} className="d-flex justify-content-center">
                            <div className="d-flex flex-column align-items-center">
                                <div className="user-org-info-profile-image-wrapper">
                                    <ProfileImage
                                        imageUrl={userDetails?.imageUrl}
                                        width={180}
                                        height={180}
                                        className="user-org-info-profile-image"
                                    />
                                </div>
                                <div className="user-org-info-user-details">
                                    <div className="user-org-info-user-primary-info">
                                        <h3>
                                            {userDetails?.firstName && userDetails?.lastName 
                                                ? `${userDetails.firstName} ${userDetails.lastName}`
                                                : userDetails?.firstName || userDetails?.lastName || ''}
                                            <br />
                                            <span className="user-org-info-username">Username: {userDetails?.username}</span>
                                        </h3>
                                        <p className="user-org-info-user-email">Email: {userDetails?.email}</p>
                                    </div>
                                    <p className="user-org-info-user-personnel-code">Personnel Code: {userDetails?.personnelCode}</p>
                                </div>
                            </div>
                        </Col>
                        
                        {activePositions.length > 0 && (
                            <Col md={6} className="d-flex align-items-center">
                                <div className="user-org-info-user-positions w-100">
                                    <h6 className="mb-3">Current Positions</h6>
                                    {activePositions.map((position, index) => (
                                        <div key={index} className="user-org-info-position-item">
                                            <p className="user-org-info-position-title mb-2">
                                                Position: {position.title} - Role: {position.role} - Importance: {position.roleImportance}
                                            </p>
                                            {position.unit !== 'Non-Organizational' && (
                                                <p className="user-org-info-position-unit mb-0">Unit: {position.unit}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        )}
                    </Row>
                </div>

                {/* Metrics Section */}
                <Row className="user-org-info-metrics-section">
                    {metrics.map((metric, index) => (
                        <Col key={index}>
                            <div className="user-org-info-metric-card">
                                <div className="user-org-info-metric-icon">
                                    {metric.icon}
                                </div>
                                <div className="user-org-info-metric-value">
                                    {metric.value}
                                </div>
                                <div className="user-org-info-metric-label">
                                    {metric.label}
                                </div>
                                {metric.subLabel && (
                                    <div className="user-org-info-metric-sublabel">
                                        {metric.subLabel}
                                    </div>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>

                {/* Charts Section */}
                <Row className="user-org-info-charts-section">
                    <Col md={6}>
                        <div className="user-org-info-chart-card">
                            <h6>
                                <BsGraphUp />
                                Added Value Over Time
                            </h6>
                            <div className="user-org-info-chart-container">
                                {prepareAddedValueChartData() ? (
                                    <Line 
                                        data={prepareAddedValueChartData()} 
                                        options={addedValueChartOptions}
                                    />
                                ) : (
                                    <div className="user-org-info-chart-placeholder">
                                        <BsGraphUp />
                                        No appointment data available
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="user-org-info-chart-card">
                            <h6>
                                <BsPieChartFill />
                                Role Distribution
                            </h6>
                            <div className="user-org-info-chart-container">
                                {prepareRoleDistributionData() ? (
                                    <Bar 
                                        data={prepareRoleDistributionData()} 
                                        options={roleDistributionChartOptions}
                                    />
                                ) : (
                                    <div className="user-org-info-chart-placeholder">
                                        <BsPieChartFill />
                                        No role data available
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default UserOrgInfo; 